import { FilterBar, Item } from "@bps/fluent-ui";
import { OutboundEmailAuditArgs } from "@libs/api/gateways/plt-comms/plt-comms-gateway.dtos";

export interface EmailAuditingFilterValues {
  searchTerm?: string;
  startDate?: Date;
  endDate?: Date;
}

interface EmailAuditingFilterProps {
  setFilterArgs: React.Dispatch<
    React.SetStateAction<OutboundEmailAuditArgs | undefined>
  >;
  setFilterIssue: React.Dispatch<React.SetStateAction<string | undefined>>;
}

// Mirrors the server-side rules in GetEmailArgsValidator - kept in sync so the
// API is only called once a request would actually pass validation.
const MIN_SEARCH_TERM_LENGTH = 5;
const MAX_DATE_RANGE_DAYS = 5;
const MS_PER_DAY = 86400000; // 1000 * 60 * 60 * 24

// Returns a human-readable reason the current filter can't be searched yet,
// or undefined once it's valid. Surfaced by the parent instead of silently
// showing "no records found" for an incomplete/invalid filter.
const getEmailAuditingFilterIssue = (
  values: EmailAuditingFilterValues
): string | undefined => {
  const { searchTerm, startDate, endDate } = values;

  if (!searchTerm || searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
    return `Enter a search term of at least ${MIN_SEARCH_TERM_LENGTH} characters.`;
  }

  if (!startDate || !endDate) {
    return "Select both a start date and an end date.";
  }

  if (endDate < startDate) {
    return "The end date must be on or after the start date.";
  }

  const startOfStartDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );

  const startOfEndDate = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  const dayRange = Math.abs(
    (startOfEndDate.getTime() - startOfStartDate.getTime()) / MS_PER_DAY
  );

  if (dayRange > MAX_DATE_RANGE_DAYS) {
    return `The date range must be ${MAX_DATE_RANGE_DAYS} days or fewer.`;
  }

  return undefined;
};

export const EmailAuditingFilter = ({
  setFilterArgs,
  setFilterIssue
}: EmailAuditingFilterProps) => {
  const filterItems: Item<EmailAuditingFilterValues>[] = [
    {
      name: "searchTerm",
      type: "searchBox",
      stickItem: true,
      props: {
        placeholder: "Search by email or subject",
        styles: { root: { width: 400 } },
        removeSpecialCharacters: false
      }
    },
    {
      name: "datesRangePicker",
      type: "datesRangePicker",
      props: {
        startDateProps: {
          label: "Start date",
          maxDate: new Date()
        },
        endDateProps: { label: "End date" },
        startDateName: "startDate",
        endDateName: "endDate",
        placeholder: "Date(s)",
        label: "Date(s)",
        styles: { fieldGroup: { width: "100%" } }
      }
    }
  ];

  return (
    <FilterBar<EmailAuditingFilterValues>
      items={filterItems}
      initialValues={{
        searchTerm: undefined,
        startDate: undefined,
        endDate: undefined
      }}
      onChange={filter => {
        const { values } = filter.state;
        const issue = getEmailAuditingFilterIssue(values);
        setFilterArgs(issue ? undefined : { ...values });
        setFilterIssue(issue);
      }}
    />
  );
};
